import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  RowSelectionState,
  ColumnFiltersState
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper
} from '@mui/material';
import { useEmployeeBonusSelectionColumns } from './columns';
import { useListEmployees } from '../../../../queries/employee';
import { useStore } from '../../../../hooks/useStore';
import { useState, useEffect, useMemo } from 'react';
import { globalFilterFn } from '../../../util/filter';
import { useParams } from 'react-router';
import {
  CreateEmployeeExportRequest,
  CreatePayrollExportRequest,
  CreateReceiptExportRequest,
  EmployeeListItem
} from '@pxtra-client-lib-legacy/gen';
import { TableToolBar } from '../shared/toolbar';
import Pagination from '../shared/pagination';
import { DEFAULT_PAGE_SIZE, scrollableTableContainerSx } from '../shared/utils';
import { exportFlows } from '../../../../pages/exports/config';
import { observer } from 'mobx-react-lite';
import { DataTableLoadingBar } from '../shared/loading-bar';
import EmptyTable from '../../../icons/svg/empty-table.svg';
import { useTranslation } from 'react-i18next';
import { SortableTableHeader } from '../shared/sortable-header';
import { useStepper } from '../../../../pages/actions/partials/actionStepperPartial';
import { useActions } from '../../../../hooks/useActions';
import { useCreateBonus } from '../../../../pages/actions/createBonus/hooks/useCreateBonus';
import { cn } from '../../../util';

type Props = {
  employeesWithMissingData?: EmployeeListItem[];
  editProfilePage: 'position' | 'personal';
};

export const EmployeeBonusSelectionTable = observer(
  ({ employeesWithMissingData = [], editProfilePage }: Props) => {
    const { companyStore, exportFlowStore } = useStore();
    const params = useParams();
    const { t } = useTranslation();

    const { data, isFetching } = useListEmployees(companyStore.company?.id, {
      enabled: !!companyStore.company?.id
    });

    const { newBonus } = useCreateBonus();
    const { editMode } = useStepper();

    const employeesData = useMemo(() => {
      const assignedEmployeeIds = new Set(newBonus.employees || []);
      return (data?.pages?.flat() ?? []).filter(
        (employee) =>
          (employee.status !== 'RESIGNED' && employee.status !== 'DEACTIVATION_DELAYED') ||
          (employee.id !== undefined && assignedEmployeeIds.has(employee.id))
      );
    }, [data, newBonus.employees]);

    const missingDataEmployeeIds = useMemo(
      () => new Set(employeesWithMissingData.map((emp) => emp.id).filter(Boolean)),
      [employeesWithMissingData]
    );

    const getRowIsDisabled = useMemo(() => {
      const isBirthdayOneTime =
        newBonus.specialOccasionType === 'BIRTHDAY' && newBonus.frequency === 'ONE_TIME';

      if (editMode && newBonus.editingId && isBirthdayOneTime) {
        const assignedEmployeeIds = new Set(newBonus.initialEmployees || []);
        return (employee: EmployeeListItem) =>
          !!(employee.id && !assignedEmployeeIds.has(employee.id));
      }

      return undefined;
    }, [
      editMode,
      newBonus.editingId,
      newBonus.specialOccasionType,
      newBonus.frequency,
      newBonus.employees,
      newBonus.initialEmployees
    ]);

    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: DEFAULT_PAGE_SIZE
    });
    const [globalFilter, setGlobalFilter] = useState('');
    const [rowSelection, setRowSelection] = useState<RowSelectionState>(() => {
      const selectedIds =
        (
          exportFlowStore.data as
            CreateEmployeeExportRequest | CreateReceiptExportRequest | CreatePayrollExportRequest
        )?.employees_query?.ids || [];
      return selectedIds.reduce((acc, id) => ({ ...acc, [id]: true }), {});
    });
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const columns = useEmployeeBonusSelectionColumns();

    const { selectedEmployees, setSelectedEmployees } = useActions();

    useEffect(() => {
      if (
        params.type &&
        !exportFlowStore.getIsInitialized(params.type as keyof typeof exportFlows)
      ) {
        exportFlowStore.initializeFlow(params.type as keyof typeof exportFlows);
      }
    }, [params.type]);

    useEffect(() => {
      setRowSelection(selectedEmployees);
    }, [selectedEmployees]);

    const onRowSelectionChange = (
      updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)
    ) => {
      const updatedSelection =
        typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue;

      const selectedIds = Object.keys(updatedSelection);

      setRowSelection(updatedSelection);
      setSelectedEmployees(updatedSelection);

      exportFlowStore.updateData({ employees_query: { ids: selectedIds } });
    };

    const table = useReactTable({
      data: employeesData,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      globalFilterFn: globalFilterFn,
      onGlobalFilterChange: setGlobalFilter,
      onRowSelectionChange,
      getRowId: (row) => row.id ?? 'MISSING_ROW_ID',
      onColumnFiltersChange: setColumnFilters,
      enableRowSelection: (row) => (getRowIsDisabled ? !getRowIsDisabled(row.original) : true),
      state: {
        pagination,
        globalFilter,
        rowSelection,
        columnFilters
      },
      meta: {
        getRowIsDisabled
      },
      initialState: {
        columnVisibility: {
          has_missing_fields: false,
          department: false,
          cost_center: false,
          active_benefit: false,
          budget_exhausted: false,
          entry_date: false
        }
      }
    });

    return (
      <Paper className="overflow-hidden flex flex-col h-[calc(55vh-20px)]">
        <TableToolBar table={table} />

        <TableContainer sx={scrollableTableContainerSx()}>
          <Table stickyHeader className="bg-white" size="small">
            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <SortableTableHeader
                      key={header.id}
                      header={header}
                      isLoading={isFetching}
                      hasData={employeesData.length > 0}
                    />
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell
                  sx={{
                    padding: 0,
                    borderBottom: 'none',
                    backgroundColor: 'transparent'
                  }}
                  colSpan={columns.length}
                >
                  {isFetching && <DataTableLoadingBar isLoading={isFetching} />}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="!border-none">
                    <div className="flex flex-col items-center justify-center">
                      <img
                        src={EmptyTable}
                        alt={t('export:EMPTY_EMPLOYEE_SELECTION_TABLE')}
                        className="w-64 h-64"
                      />
                      <p className={'mt-4 text-grey-500'}>
                        {t('export:EMPTY_EMPLOYEE_SELECTION_TABLE')}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => {
                  const isDisabled = getRowIsDisabled ? getRowIsDisabled(row.original) : false;
                  const isMissingData = missingDataEmployeeIds.has(row.original.id);
                  const isSelected = row.getIsSelected();

                  return (
                    <TableRow
                      key={row.id}
                      className={cn(
                        '!border-none hover:bg-heaven-100',
                        isDisabled && 'opacity-50  pointer-events-none',
                        isSelected && 'bg-heaven-200 hover:bg-heaven-300',
                        isMissingData && 'bg-warning-lightest'
                      )}
                      onClick={(e) => {
                        if (isDisabled) return;
                        e.stopPropagation();
                      }}
                      onDoubleClick={(e) => {
                        if (isDisabled) return;
                        e.stopPropagation();
                        e.preventDefault();

                        const employeeId = row.original.id;

                        setTimeout(() => {
                          const targetPath = `/employees/${employeeId}/data/${editProfilePage}/edit`;
                          const baseUrl = window.location.origin;
                          window.open(`${baseUrl}${targetPath}`, '_blank', 'noopener,noreferrer');
                        }, 0);

                        return false;
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell className="py-2 border-grey-100" key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination table={table} />
      </Paper>
    );
  }
);
